import React, { useEffect, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import {
    Box,
    Button,
    Card,
    Collapse,
    Typography,
} from '@sunwisesoftware/sunwise-ui';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { createStructuredSelector } from 'reselect';

import EmptyState from 'common/components/EmptyState';
import { POLYGON_SEGMENT } from 'common/components/segment/constants';
import { handleOpenChangeSegments } from 'common/components/segment/helpers';
import MenuOptions from 'common/components/segment/MenuOptions';
import SegmentErrors from 'common/components/segment/SegmentErrors';
import { getDesignCode } from 'common/utils/helpers/systemsValues';

import EmptyLayout from 'resources/EmptyLayout.svg';

import * as actions from '../actions';
import * as selectors from '../selectors';

import SegmentEditCard from './SegmentEditCard';

const filterSegments = (segments, selectedType) => {
    if (selectedType === 'all') return segments;
    return segments.filter(
        (segment) => segment.type === parseInt(selectedType),
    );
};

const SegmentsListCard = ({
    commercialOfferId,
    handleClickAdd,
    handleClickDelete,
    handleClickEditSegment,
    handleOnDuplicateSegment,
    handleSelectSegment,
    initialValues,
    isDisabledEdition,
    isEditionMode,
    onCloseDrawer,
    segments,
    selectedSegmentId,
    updateSegments,
}) => {
    const { t } = useTranslation();
    const isDisabled = isEditionMode;
    const [segmentsList, setSegmentsList] = useState([]);
    const [selectedType, setSelectedType] = useState('all');

    useEffect(() => {
        const filteredSegments = filterSegments(segments, selectedType);
        setSegmentsList(filteredSegments);
    }, [segments]);

    useEffect(() => {
        if (!isEmpty(initialValues)) {
            const filteredSegments = filterSegments(segments, 'all');
            setSegmentsList(filteredSegments);
            setSelectedType('all');
        }
    }, [initialValues]);

    const polygonContent = ({ item }) => {
        const desingCode = getDesignCode(item.system_uid);
        return (
            <>
                <Typography variant="body2" sx={{ mt: 1 }}>
                    {item?.panel?.name || 'N/A'}
                </Typography>
                <Typography variant="body2">
                    {t('Panel', {
                        count: parseInt(item?.quantity_enabled_modules),
                    })}
                    : {item.quantity_enabled_modules} u
                </Typography>
                <Typography variant="body2">
                    {t('System size')}:{' '}
                    {(item.quantity_enabled_modules * item.panel.watts) / 1000}{' '}
                    kW
                </Typography>
                <Typography variant="body2">{item?.system_name}</Typography>
                <Typography variant="body2">{desingCode}</Typography>
            </>
        );
    };

    const renderContent = ({ item }) => {
        if (item.type === POLYGON_SEGMENT) return polygonContent({ item });
    };

    const handleOnClickAdd = (type) => handleClickAdd({ type });

    const { handleSubmit, control, reset, setValue, trigger, watch } = useForm({
        defaultValues: initialValues,
    });
    if (segments?.length === 0) {
        return (
            <EmptyState
                customCaption={
                    <Typography fontWeight="bold" variant="body2">
                        {t("Looks like you don't have segments yet. Add one!")}
                    </Typography>
                }
                onClick={() => handleClickAdd({ type: POLYGON_SEGMENT })}
                sizeImage={160}
                srcImage={EmptyLayout}
                sxCard={{ boxShadow: 'none !important' }}
                textButton={t('Add segment')}
            />
        );
    }

    return (
        <>
            {segmentsList.map((item) => {
                const errorsArrays = item?.segment_errors?.error?.arrays || [];
                const errorsSystem = item?.segment_errors?.error?.system || [];
                const warningsArrays =
                    item?.segment_errors?.warning?.arrays || [];
                const warningsSystem =
                    item?.segment_errors?.warning?.system || [];
                const errors = [...errorsArrays, ...errorsSystem];
                const warnings = [...warningsArrays, ...warningsSystem];
                return (
                    <Card key={item.id} sx={{ mt: 2 }}>
                        <Card.Body>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    position: 'relative', // Make the parent container relative
                                }}
                            >
                                <Box
                                    onClick={() => {
                                        if (isDisabled) return;
                                        handleSelectSegment(item, segments);
                                        handleOpenChangeSegments({
                                            item,
                                            segments,
                                            updateSegments,
                                        });
                                    }}
                                    sx={{
                                        cursor: 'pointer',
                                        flexGrow: 1,
                                        width: '70%',
                                    }}
                                >
                                    {(!isEditionMode ||
                                        item.id !== selectedSegmentId) && (
                                        <Typography
                                            fontWeight="bold"
                                            noWrap
                                            sx={{
                                                fontSize: '14px',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                            }}
                                            variant="body2"
                                        >
                                            {item.name}
                                        </Typography>
                                    )}
                                </Box>

                                <Box sx={{ ml: 'auto', mr: 0 }}>
                                    <MenuOptions
                                        commercialOfferId={commercialOfferId}
                                        handleClickDelete={handleClickDelete}
                                        handleOnDuplicateSegment={
                                            handleOnDuplicateSegment
                                        }
                                        handleClickEditSegment={
                                            handleClickEditSegment
                                        }
                                        isDisabled={
                                            isDisabled || isDisabledEdition
                                        }
                                        isHidden={
                                            item.id !== selectedSegmentId ||
                                            !isEmpty(initialValues)
                                        }
                                        item={item}
                                    />
                                </Box>
                            </Box>

                            <SegmentErrors
                                baseId={item.id}
                                errors={errors}
                                severity="error"
                            />

                            <SegmentErrors baseId={item.id} errors={warnings} />

                            <Collapse
                                key={`collapse-${item.id}-${item.open}`}
                                in={
                                    item.open ||
                                    (!isEmpty(initialValues) &&
                                        item.id === initialValues.id)
                                }
                                unmountOnExit
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        cursor: 'pointer',
                                    }}
                                >
                                    {isEmpty(initialValues) &&
                                        renderContent({ item })}

                                    {!isEmpty(initialValues) &&
                                        item.id === initialValues.id && (
                                            <SegmentEditCard
                                                commercialOfferId={
                                                    commercialOfferId
                                                }
                                                onCloseDrawer={onCloseDrawer}
                                                handleSubmit={handleSubmit}
                                                control={control}
                                                reset={reset}
                                                setValue={setValue}
                                                segment={item}
                                                trigger={trigger}
                                                watch={watch}
                                            />
                                        )}
                                </Box>
                            </Collapse>
                        </Card.Body>
                    </Card>
                );
            })}
            <Button
                disabled={isDisabled || isDisabledEdition}
                fullWidth
                onClick={() => handleOnClickAdd(POLYGON_SEGMENT)}
                startIcon={<AddIcon />}
                variant="outlined"
            >
                {t('Add')}
            </Button>
        </>
    );
};

const mapStateToProps = createStructuredSelector({
    initialValues: selectors.getInitialValues,
});

const mapDispatchToProps = (dispatch) => ({
    resetForm: () => dispatch(actions.resetForm()),
    updateSegment: (segments) => dispatch(actions.updateSegment(segments)),
    updateSegments: (segments) => dispatch(actions.updateSegments(segments)),
});

SegmentsListCard.propTypes = {
    commercialOfferId: PropTypes.string,
    handleClickAdd: PropTypes.func,
    handleClickDelete: PropTypes.func,
    handleClickEditSegment: PropTypes.func,
    handleOnDuplicateSegment: PropTypes.func,
    handleSelectSegment: PropTypes.func,
    initialValues: PropTypes.object,
    isDisabledEdition: PropTypes.bool,
    isEditionMode: PropTypes.bool,
    onCloseDrawer: PropTypes.func,
    segments: PropTypes.array,
    selectedSegmentId: PropTypes.string,
    updateSegments: PropTypes.func,
};

export default compose(connect(mapStateToProps, mapDispatchToProps))(
    SegmentsListCard,
);
